import {EMPTY_CALLBACK} from "@/shared/utils/misc";
import {calculateDurationSince, formatDuration} from "@/shared/utils/datefunctions";
import {RunningTimeStatus, UserSumsChartRange, WorkingTimeTypeCategory} from "@/shared/timrTypes";
import {UserSumsWorkingTime, WorkDurations, WorkingTimeStatus} from "@/shared/timr";

declare const c3: typeof import("c3");
declare const d3: typeof import("d3");

export interface UserSumsWorkingTimeChart {
	workingTimeUser: number;
	targetWorkDurations: number;
	popoverContentElement: JQuery;
}

export class WorkingTimeStatistics {

	private container: JQuery;
	private workingTimeUserSums: UserSumsWorkingTime;
	private targetWorkDurations: WorkDurations;
	private todayChart: c3.ChartAPI;
	private thisWeekChart: c3.ChartAPI;
	private thisMonthChart: c3.ChartAPI;

	constructor(container: JQuery) {
		this.container = container;
		this.init();
	}

	/**
	 * Updates the working time statistics based on the provided working time status.
	 *
	 * @param workingTimeStatus - The current status of the working time, including user sums, running working time, and target durations.
	 */
	public updateStatistics(workingTimeStatus: WorkingTimeStatus) {
		const {workingTimeUserSums, runningWorkingTime, targetWorkDurations} = workingTimeStatus;
		if (workingTimeUserSums) {
			const workingTimeUserSumsCopy = {...workingTimeUserSums};

			// take current running working time into account
			// DEVNOTE: Don't add a running break time to durationDay, durationWeek and durationMonth (TIMR-7654)
			if (runningWorkingTime && runningWorkingTime.workingTimeType?.category !== WorkingTimeTypeCategory.BreakTime) {
				// if runningWorkingTime is paused the breakTime isn't updated yet
				const runningDurationMs = Math.trunc(calculateDurationSince(
					runningWorkingTime.start,
					runningWorkingTime.breakTime,
					runningWorkingTime.status === RunningTimeStatus.PAUSED ? runningWorkingTime.pausedAt : null
				).total('minutes')) * 60_000;
				workingTimeUserSumsCopy.durationDay += runningDurationMs;
				workingTimeUserSumsCopy.durationWeek += runningDurationMs;
				workingTimeUserSumsCopy.durationMonth += runningDurationMs;
			}
			this.updateValues(workingTimeUserSumsCopy, targetWorkDurations);
		}
	}

	protected renderChartPopover(chartRange: UserSumsChartRange) {
		const durations = this.selectChartRange(chartRange);
		if (durations.targetWorkDurations !== 0) {
			const ratioWorkingTimeTarget = durations.workingTimeUser / durations.targetWorkDurations;
			if (durations.workingTimeUser <= durations.targetWorkDurations) {
				return `<table class="statistics-chart-popover-table">
								<tr>
									<td class="row-1-description"><i class="fa fa-circle statistics-chart-popover-circle-working-time-actual"></i> <span>${messages["statisticsPanel.workingTime.actual"]}</span></td>
									<td class="duration-width row-1-value text-right">${formatDuration(durations.workingTimeUser)}</td>
									<td class="duration-width row-1-ratio text-right">(${d3.format(".2%")(ratioWorkingTimeTarget)})</td>
								</tr>
								<tr>
									<td class="row-2-description"><i class="fa fa-circle statistics-chart-popover-circle-working-time-remaining"></i> <span>${messages["statisticsPanel.workingTime.remaining"]}</span></td>
									<td class="duration-width row-2-value text-right">${formatDuration(durations.targetWorkDurations - durations.workingTimeUser)} </td>
									<td class="duration-width row-2-ratio text-right">(${d3.format(".2%")(1 - ratioWorkingTimeTarget)})</td>
								</tr>
							</table>`;
			} else {
				const overtime = durations.workingTimeUser - durations.targetWorkDurations;
				return `<table class="statistics-chart-popover-table">
								<tr>
									<td class="row-1-description"><i class="fa fa-circle statistics-chart-popover-circle-working-time-overtime"></i> <span>${messages["statisticsPanel.workingTime.overtime"]}</span></td>
									<td class="duration-width row-1-value text-right">${formatDuration(overtime)}</td>
									<td class="duration-width row-1-ratio text-right"></td>
								</tr>
								<tr>
									<td class="row-2-description"><i class="fa fa-circle statistics-chart-popover-circle-working-time-actual"></i> <span>${messages["statisticsPanel.workingTime.actual"]}</span></td>
									<td class="duration-width row-2-value text-right">${formatDuration(durations.workingTimeUser)}</td>
									<td class="duration-width row-2-ratio text-right">(${d3.format(".2%")(ratioWorkingTimeTarget)})</td>
								</tr>
							</table>`;
			}
		} else {
			return `${messages["statisticsPanel.noTarget"]}`;
		}
	}

	private updateValues(workingTimeUserSums: UserSumsWorkingTime, targetWorkDurations: WorkDurations) {
		this.workingTimeUserSums = workingTimeUserSums;
		this.targetWorkDurations = targetWorkDurations;

		this.handleChartPopoverBuild(UserSumsChartRange.Day);
		this.handleChartPopoverBuild(UserSumsChartRange.Week);
		this.handleChartPopoverBuild(UserSumsChartRange.Month);

		this.container.find("#workingTimeDurationToday").text(formatDuration(workingTimeUserSums.durationDay));
		this.container.find("#workingTimeDurationThisWeek").text(formatDuration(workingTimeUserSums.durationWeek));
		this.container.find("#workingTimeDurationThisMonth").text(formatDuration(workingTimeUserSums.durationMonth));

		this.container.find("#workingTimeDurationToTargetThisDay").text(formatDuration(targetWorkDurations.workDurationPerDay));
		this.container.find("#workingTimeDurationToTargetThisWeek").text(formatDuration(targetWorkDurations.workDurationPerWeek));
		this.container.find("#workingTimeDurationToTargetThisMonth").text(formatDuration(targetWorkDurations.workDurationPerMonth));

		this.todayChart.load({
			columns: this.buildChartColumns(workingTimeUserSums.durationDay, targetWorkDurations.workDurationPerDay)
		});
		this.thisWeekChart.load({
			columns: this.buildChartColumns(workingTimeUserSums.durationWeek, targetWorkDurations.workDurationPerWeek)
		});
		this.thisMonthChart.load({
			columns: this.buildChartColumns(workingTimeUserSums.durationMonth, targetWorkDurations.workDurationPerMonth)
		});
	}

	private handleChartPopoverBuild(chartRange: UserSumsChartRange) {
		const chartRangeKey = UserSumsChartRange[chartRange];
		const containerSelector = "#workingTimeThis" + chartRangeKey + "ChartPopover";
		const popoverSelector = "this-" + chartRangeKey.toLowerCase() + "-chart-popover";

		if ($(`.${popoverSelector}`).length === 0) {
			this.container.find(containerSelector).popover("destroy").popover({
				animation: false,
				trigger: "hover",
				placement: "top",
				container: ".content-wrapper",
				delay: {show: 400, hide: 0},
				html: true,
				template: `<div class="popover ${popoverSelector} statistics-chart-popover" role="tooltip"><div class="arrow"></div><div class="popover-content"></div></div>`,
				content: () => this.renderChartPopover(chartRange)
			});
		} else {
			this.updateChartPopover(chartRange);
		}
	}

	private init() {
		this.todayChart = this.initChart("#workingTimeTodayChart");
		this.thisWeekChart = this.initChart("#workingTimeThisWeekChart");
		this.thisMonthChart = this.initChart("#workingTimeThisMonthChart");
	}

	private initChart(bindTo: string): c3.ChartAPI {
		return c3.generate({
			bindto: bindTo,
			size: {height: 110},
			interaction: {enabled: false},
			data: {
				// series colours come from CSS (components/charts.less, .c3-arc-<id>), themed there
				names: {
					overtime: messages["statisticsPanel.workingTime.overtime"],
					actual: messages["statisticsPanel.workingTime.actual"],
					remaining: messages["statisticsPanel.workingTime.remaining"]
				},
				columns: this.buildChartColumns(0, 0),
				type: "donut",
				order: null
			},
			legend: {
				show: false,
				item: {
					onclick: EMPTY_CALLBACK
				}
			},
			donut: {
				label: {
					show: false
				},
				width: 7
			}
		});
	}

	private buildChartColumns(workingTimeDuration: number, targetDuration: number): c3.PrimitiveArray[] {
		let remaining = 0;
		let overtime = 0;
		let actual = 0;
		if (targetDuration === 0) {
			remaining = 1;
		} else if (workingTimeDuration <= targetDuration) {
			remaining = targetDuration - workingTimeDuration;
			actual = workingTimeDuration;
		} else {
			overtime = Math.min(workingTimeDuration - targetDuration, targetDuration);
			actual = targetDuration - overtime;
		}
		return [
			["overtime", overtime],
			["actual", actual],
			["remaining", remaining]
		];
	}

	private selectChartRange(chartRange: UserSumsChartRange): UserSumsWorkingTimeChart {
		switch (chartRange) {
			case UserSumsChartRange.Day:
				return this.handleCase(this.workingTimeUserSums.durationDay, this.targetWorkDurations.workDurationPerDay, $(".this-day-chart-popover"));
			case UserSumsChartRange.Week:
				return this.handleCase(this.workingTimeUserSums.durationWeek, this.targetWorkDurations.workDurationPerWeek, $(".this-week-chart-popover"));
			case UserSumsChartRange.Month:
				return this.handleCase(this.workingTimeUserSums.durationMonth, this.targetWorkDurations.workDurationPerMonth, $(".this-month-chart-popover"));
		}
	}

	private handleCase(durationBillable: number, durationNotBillable: number, popoverContentElement: JQuery): UserSumsWorkingTimeChart {
		return {
			workingTimeUser: durationBillable,
			targetWorkDurations: durationNotBillable,
			popoverContentElement: popoverContentElement
		};
	}

	private updateChartPopover(chartRange: UserSumsChartRange) {
		const durations = this.selectChartRange(chartRange);
		if (durations.targetWorkDurations !== 0) {
			const ratioWorkingTimeTarget = durations.workingTimeUser / durations.targetWorkDurations;
			durations.popoverContentElement.find(".ratioWorkingTimeTarget").text("(" + d3.format(".2%")(ratioWorkingTimeTarget) + ")");
			durations.popoverContentElement.find(".workingTimeUser").text(formatDuration(durations.workingTimeUser));

			if (durations.workingTimeUser <= durations.targetWorkDurations) {
				this.handlePopoverDataUpdates(durations, false,
					messages["statisticsPanel.workingTime.actual"],
					formatDuration(durations.workingTimeUser),
					"(" + d3.format(".2%")(ratioWorkingTimeTarget) + ")",
					messages["statisticsPanel.workingTime.remaining"],
					formatDuration(durations.targetWorkDurations - durations.workingTimeUser),
					"(" + d3.format(".2%")(1 - ratioWorkingTimeTarget) + ")"
				);
			} else {
				const overtime = durations.workingTimeUser - durations.targetWorkDurations;
				this.handlePopoverDataUpdates(durations, true,
					messages["statisticsPanel.workingTime.overtime"],
					formatDuration(overtime),
					"",
					messages["statisticsPanel.workingTime.actual"],
					formatDuration(durations.workingTimeUser),
					"(" + d3.format(".2%")(ratioWorkingTimeTarget) + ")"
				);
			}
		}
	}

	private handlePopoverDataUpdates(durations: UserSumsWorkingTimeChart, overtime: boolean, row1message: string, row1value: string, row1ratio: string, row2message: string, row2value: string,
	                                 row2ratio: string) {
		const firstRowDescription = durations.popoverContentElement.find(".row-1-description");
		const firstRowDescriptionIcon = firstRowDescription.find(" > i");
		firstRowDescription.find(" > span").text(row1message);
		firstRowDescriptionIcon.toggleClass(`statistics-chart-popover-circle-working-time-actual`, !overtime);
		firstRowDescriptionIcon.toggleClass("statistics-chart-popover-circle-working-time-overtime", overtime);
		durations.popoverContentElement.find(".row-1-value").text(row1value);
		durations.popoverContentElement.find(".row-1-ratio").text(row1ratio);

		const secondRowDescription = durations.popoverContentElement.find(".row-2-description");
		const secondRowDescriptionIcon = secondRowDescription.find(" > i");
		secondRowDescription.find(" > span").text(row2message);
		secondRowDescriptionIcon.toggleClass("statistics-chart-popover-circle-working-time-remaining", !overtime);
		secondRowDescriptionIcon.toggleClass("statistics-chart-popover-circle-working-time-actual", overtime);
		durations.popoverContentElement.find(".row-2-value").text(row2value);
		durations.popoverContentElement.find(".row-2-ratio").text(row2ratio);
	}
}
